<div class="p-6">
  <!-- The tab bar already shows this label visually; the heading exists for screen readers and
       document structure, so it is visually hidden to avoid repeating the word on screen. -->
  <h1 class="sr-only">NYC Sync</h1>

  <!--
    The detail is rendered inline rather than hidden in a tooltip: it is the only place these counts
    appear, and a tooltip is unreachable by keyboard and screen reader.
  -->
  <div class="flex items-baseline gap-2 mb-4 text-sm" aria-live="polite">
    <span class="text-muted-color">NYC sync</span>
    @if (circuitBreakerLoading()) {
      <span class="text-muted-color">Loading…</span>
    } @else {
      @switch (circuitBreakerState()) {
        @case ('tripped') {
          <span class="font-semibold text-red-600">Tripped</span>
        }
        @case ('ok') {
          <span class="font-semibold text-green-700">OK</span>
        }
        @default {
          <span class="font-semibold text-muted-color">Unknown</span>
        }
      }
      <span class="text-muted-color">{{ circuitBreakerDetail() }}</span>
    }
  </div>

  <div class="flex flex-row gap-2 items-center">
    <input
      pInputText
      [ngModel]="search()"
      (ngModelChange)="onSearchChange($event)"
      placeholder="Search"
      aria-label="Search registrations"
    />
    <p-select
      [options]="filterOptions"
      [ngModel]="selectedFilter()"
      (ngModelChange)="onFilterChange($event)"
      optionLabel="label"
      optionValue="value"
      placeholder="Filter"
      ariaLabel="Filter"
    />
  </div>

  <!-- min-width instead of letting 13 columns share the viewport: below it the table scrolls
       sideways, which keeps every column readable. Without it the browser squeezes the narrow
       columns until an email address wraps one character per line and the row grows to match. -->
  <p-table
    class="mt-4 block"
    size="small"
    [value]="users()"
    [scrollable]="true"
    scrollHeight="65vh"
    [tableStyle]="{'min-width': '110rem'}"
  >
    <ng-template pTemplate="caption">
      <span class="sr-only">NYC registrations</span>
      <div class="text-sm font-normal" role="status" aria-live="polite" aria-atomic="true">
        Showing {{ users().length }} of {{ totalCount() }}{{ truncated() ? '+' : '' }} registrations
      </div>
    </ng-template>
    <ng-template pTemplate="header">
      <tr class="[&>th]:whitespace-nowrap">
        <th>Name</th>
        <th>Email</th>
        <th>Profile Status</th>
        <th>Auto Register</th>
        <th>Sync Status</th>
        <th>Sync Failures</th>
        <th>Login Status</th>
        <th>Login Failures</th>
        <th>Last Sync Error</th>
        <th>Failure Reason</th>
        <th><span class="sr-only">Flags</span></th>
        <th>Created at</th>
        <th>Id</th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-user [homeyTypedTableRow]="users()">
      @let autoCreateTag = autoCreateStatusTag(user);
      <!-- align-top: one long failure reason otherwise leaves every other cell in the row floating
           in the middle of a band of whitespace. -->
      <tr class="align-top">
        <td class="whitespace-nowrap">
          <homey-user-link [id]="user.id" [label]="userDisplayName(user)"/>
        </td>
        <td class="max-w-56 truncate" [title]="user.email">{{ user.email }}</td>
        <td>
          <p-tag [severity]="nycProfileStatusToTag(user.nycProfileStatus)">{{ user.nycProfileStatus }}</p-tag>
        </td>
        <td>
          @if (user.nycProfileStatus === 'complete') {
            <span class="text-gray-400">&mdash;</span>
          } @else {
            <p-tag [severity]="autoCreateTag.severity">{{ autoCreateTag.value }}</p-tag>
          }
        </td>
        <td>
          <p-tag [severity]="lastSyncStatusToTag(user.lastSyncStatus)">{{ user.lastSyncStatus }}</p-tag>
        </td>
        <td>
          @if (user.syncFailedCount > 0) {
            <span class="text-red-600 font-semibold">{{ user.syncFailedCount }}</span>
          } @else {
            <span class="text-gray-400">&mdash;</span>
          }
        </td>
        <td>
          <p-tag [severity]="loginStatusToTag(user.loginStatus)">{{ user.loginStatus }}</p-tag>
        </td>
        <td>
          @if (user.loginFailedCount > 0) {
            <span class="text-red-600 font-semibold">{{ user.loginFailedCount }}</span>
          } @else {
            <span class="text-gray-400">&mdash;</span>
          }
        </td>
        <td class="text-xs">
          @if (user.lastSyncError) {
            <span class="line-clamp-2 max-w-48 break-words" [title]="user.lastSyncError">{{ user.lastSyncError }}</span>
          } @else {
            <span class="text-gray-400">&mdash;</span>
          }
        </td>
        <td class="text-xs">
          @if (user.autoCreateFailureReason) {
            <span class="line-clamp-3 max-w-96 break-words" [title]="user.autoCreateFailureReason">
              {{ user.autoCreateFailureReason }}
            </span>
          } @else {
            <span class="text-gray-400">&mdash;</span>
          }
        </td>
        <td>
          @if (user.ignore) {
            <p-tag severity="danger">IGNORED</p-tag>
          }
        </td>
        <td class="whitespace-nowrap">{{ user.createdAt | date: dateTimeFormat }}</td>
        <td class="font-mono text-xs text-muted-color whitespace-nowrap">{{ user.id }}</td>
      </tr>
    </ng-template>
    <!-- Without this an empty result renders as bare column headers, which reads as a broken
         request rather than an answer. -->
    <ng-template pTemplate="emptymessage">
      <tr>
        <td colspan="13" class="text-center">No registrations match this search</td>
      </tr>
    </ng-template>
  </p-table>
</div>
